import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { FiMaximize2, FiRefreshCw } from 'react-icons/fi';
import { db } from '../firebase';
import useAuthStore from '../stores/authStore';
import { communityApi, motionReviewApi, readableError } from './api';
import { CLASS_LIST, className, displayClass } from './classList';
import { EvidenceImage, ImageViewer, formatCaptureTime } from './ReviewScreen';
import MotionAdminCase from './MotionAdminCase';
import { createMotionAdminSaveQueue } from './motionAdminSaveQueue';
import { AUTO_FLAGGED_REVIEW_DIRECTIONS, MOTION_REVIEW_SECTIONS, buildAdminReviewUrl } from './adminReviewSections';
import './community.css';
import './motionAdmin.css';

function pendingAge(value) {
  const milliseconds = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(milliseconds)) return '';
  const hours = Math.max(0, Math.floor(milliseconds / 3600000));
  if (hours < 1) return 'less than an hour old';
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} old`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} old`;
}

function AdminCase({ task, onResolve, onRefresh }) {
  const [outcome, setOutcome] = useState('');
  const [correctedClassId, setCorrectedClassId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showImage, setShowImage] = useState(false);
  const closeImage = useCallback(() => setShowImage(false), []);
  const counts = task.counts || {};
  const votes = Object.entries(task.suggestedSpecies || {}).sort((left, right) => right[1] - left[1]);
  const submit = async (event) => {
    event.preventDefault();
    if (!outcome || busy) return;
    setBusy(true); setError('');
    try {
      await onResolve({ taskId: task.id, outcome, correctedClassId: outcome === 'wrong' && correctedClassId !== '' ? Number(correctedClassId) : null });
    } catch (requestError) { setError(readableError(requestError)); }
    finally { setBusy(false); }
  };
  return <article className="cr-admin-card">
    <div><div className="cr-photo-wrap"><EvidenceImage task={task} /><button type="button" className="cr-enlarge" onClick={() => setShowImage(true)} aria-label={`Enlarge screenshot ${task.id}`}><FiMaximize2 aria-hidden="true" /><span>Enlarge image</span></button></div><p className="cr-small cr-muted">{task.cameraLabel || 'PTZ camera'} · {formatCaptureTime(task.capturedAt)} SAST</p></div>
    <div>
      <h2>AI said {className(task.originalClassId)}</h2>
      <span className="cr-status-badge">{task.status === 'closed' ? `Closed · ${task.outcome === 'wrong' ? 'Wrong' : 'Correct'}` : task.needsReview ? 'Needs staff review · still open to volunteers' : 'Open to volunteers'}</span>
      <div className="cr-admin-votes"><span>{counts.total || 0} reviews</span><span>{counts.correct || 0} Correct</span><span>{counts.wrong || 0} Wrong</span><span>{counts.unsure || 0} Not sure</span></div>
      <p className="cr-small"><strong>Suggested animals:</strong> {votes.length ? votes.map(([id, count]) => `${className(id)} (${count})`).join(', ') : 'None suggested'}</p>
      {task.resolutionSource && <p className="cr-small cr-muted">Resolved by {['staff', 'admin'].includes(task.resolutionSource) ? 'staff' : 'community agreement'}{task.correctedClassId !== null && task.correctedClassId !== undefined ? ` · ${className(task.correctedClassId)}` : ''}.</p>}
      <form onSubmit={submit}>
        <label>Staff decision<select aria-label={`Staff decision for ${task.id}`} value={outcome} onChange={(event) => { setOutcome(event.target.value); if (event.target.value !== 'wrong') setCorrectedClassId(''); }} disabled={busy} required>
          <option value="">Choose a decision</option><option value="correct">The AI identification is correct</option><option value="wrong">The AI identification is wrong</option>
        </select></label>
        {outcome === 'wrong' && <label>Actual animal (optional)<select aria-label={`Actual animal for ${task.id}`} value={correctedClassId} onChange={(event) => setCorrectedClassId(event.target.value)} disabled={busy}>
          <option value="">No species correction</option>{CLASS_LIST.filter((item) => item.id !== Number(task.originalClassId)).map((item) => <option key={item.id} value={item.id}>{displayClass(item)}</option>)}
        </select></label>}
        {error && <p className="cr-error" role="alert">{error}</p>}
        <button className="cr-primary" type="submit" disabled={busy || !outcome}>{busy ? 'Saving…' : 'Save staff decision'}</button>
        <p className="cr-small cr-muted">Saves the review outcome. The original sighting stays unchanged.</p>
      </form>
    </div>
    {showImage && <ImageViewer task={task} onClose={closeImage} onRefresh={() => { setShowImage(false); onRefresh(); }} />}
  </article>;
}

function AdminWorkspace({ api, pipeline = 'yolo' }) {
  const motion = pipeline === 'motion';
  const separateStats = motion && typeof api.adminStats === 'function';
  const mediaName = motion ? 'clips' : 'screenshots';
  const cameraField = motion ? 'communityMotionReviewEnabled' : 'communityReviewEnabled';
  const [filter, setFilter] = useState('needsReview');
  const [autoFlaggedDirection, setAutoFlaggedDirection] = useState('all');
  const [saveQueue] = useState(() => createMotionAdminSaveQueue(api));
  const [, renderSaves] = useState(0);
  useEffect(() => saveQueue.subscribe(() => renderSaves((value) => value + 1)), [saveQueue]);
  useEffect(() => {
    const beforeUnload = (event) => {
      if (motion && (saveQueue.pendingCount || saveQueue.failures.length)) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [motion, saveQueue]);
  const [tasks, setTasks] = useState([]);
  const loadedPages = useRef(1);
  const [stats, setStats] = useState({});
  const [statChanges, setStatChanges] = useState({});
  const previousStats = useRef(null);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statsLoading, setStatsLoading] = useState(separateStats);
  const [statsError, setStatsError] = useState('');
  const statsRequestId = useRef(0);
  const statsRetryTimer = useRef(null);
  const loadStatsRef = useRef(null);
  const [cameras, setCameras] = useState([]);
  const [cameraError, setCameraError] = useState('');
  const [cameraSaving, setCameraSaving] = useState('');
  const [notice, setNotice] = useState('');
  const requestId = useRef(0);
  const pageRequestInFlight = useRef(false);
  const automaticPages = useRef(new Set());
  const reviewList = useRef(null);
  const adoptStats = useCallback((nextStats, savesVersion, independent = false) => {
    if (motion && !(independent ? saveQueue.acceptStats(savesVersion) : saveQueue.acceptRefresh(savesVersion))) return false;
    const changes = {};
    for (const key of ['pending', 'needsReview', 'closed', 'reviewedToday', 'flagged', 'autoFlagged']) {
      const before = previousStats.current?.[key];
      const current = nextStats[key];
      if (Number.isFinite(before) && Number.isFinite(current) && current !== before) changes[key] = current - before;
    }
    setStatChanges(changes);
    setStats(nextStats);
    previousStats.current = nextStats;
    return true;
  }, [motion, saveQueue]);
  const loadStats = useCallback(async () => {
    const request = ++statsRequestId.current;
    const savesVersion = saveQueue.version;
    window.clearTimeout(statsRetryTimer.current);
    setStatsLoading(true); setStatsError('');
    try {
      const result = await api.adminStats();
      if (request !== statsRequestId.current) return null;
      if (!adoptStats(result.stats || {}, savesVersion, true)) {
        // A save won the race. Retry once the local writes settle, rather than
        // leaving initial totals blank or querying repeatedly during a burst.
        const retryWhenSettled = () => {
          if (request !== statsRequestId.current) return;
          if (saveQueue.pendingCount) statsRetryTimer.current = window.setTimeout(retryWhenSettled, 250);
          else loadStatsRef.current?.();
        };
        statsRetryTimer.current = window.setTimeout(retryWhenSettled, 250);
        return null;
      }
      return { request, savesVersion };
    } catch (requestError) {
      if (request === statsRequestId.current) setStatsError(readableError(requestError));
      return null;
    } finally { if (request === statsRequestId.current) setStatsLoading(false); }
  }, [api, adoptStats, saveQueue]);
  loadStatsRef.current = loadStats;
  const load = useCallback(async (after = null) => {
    if (after && pageRequestInFlight.current) return;
    const request = ++requestId.current;
    pageRequestInFlight.current = true;
    if (!after) automaticPages.current.clear();
    const savesVersion = saveQueue.version;
    setLoading(true); setError('');
    try {
      // Flag times can move while this page is open. Re-read the loaded prefix
      // from its head, including on Load more, instead of mixing stale pages.
      const liveFlags = motion && filter === 'flagged';
      const pageLimit = liveFlags ? loadedPages.current + Number(Boolean(after)) : 1;
      let pageCursor = liveFlags ? null : after;
      let result; let pagesRead = 0;
      const freshTasks = new Map();
      const visitedCursors = new Set();
      do {
        const page = await api.admin({ filter, ...(motion && filter === 'autoFlagged' && autoFlaggedDirection !== 'all' ? { autoFlaggedDirection } : {}), ...(pageCursor ? { cursor: pageCursor } : {}), ...(separateStats || pagesRead ? { includeStats: false } : {}) });
        if (request !== requestId.current) return;
        for (const task of page.tasks || []) if (!freshTasks.has(task.id)) freshTasks.set(task.id, task);
        result = { ...page, stats: pagesRead ? result.stats : page.stats };
        pagesRead += 1;
        pageCursor = page.nextCursor;
        if (pageCursor && visitedCursors.has(pageCursor)) throw new Error('The review page changed while loading. Please refresh.');
        if (pageCursor) visitedCursors.add(pageCursor);
      } while (liveFlags && pageCursor && pagesRead < pageLimit);
      result.tasks = [...freshTasks.values()];
      if (liveFlags) loadedPages.current = pagesRead;
      if (request !== requestId.current) return;
      const loadedTasks = result.tasks || [];
      setTasks((previous) => {
        if (!liveFlags) return after ? [...previous, ...loadedTasks.filter((task) => !previous.some((item) => item.id === task.id))] : loadedTasks;
        const oldTasks = new Map(previous.map((task) => [task.id, task]));
        const fetchedAt = Date.now();
        return loadedTasks.map((task) => {
          const old = oldTasks.get(task.id);
          // Metadata refreshes must not restart every playing video. Keep its
          // 15-minute URL for at most 10 minutes; explicit Refresh renews it.
          const reuse = old && after && fetchedAt - old.mediaFetchedAt < 600000 &&
            ['videoUrl', 'maskVideoUrl', 'fullClipUrl'].every((field) => (old[field] || '').split('?')[0] === (task[field] || '').split('?')[0]);
          return reuse ? { ...task, videoUrl: old.videoUrl, maskVideoUrl: old.maskVideoUrl, fullClipUrl: old.fullClipUrl, mediaFetchedAt: old.mediaFetchedAt }
            : { ...task, mediaFetchedAt: fetchedAt };
        });
      });
      if (!separateStats && (!after || previousStats.current === null)) adoptStats(result.stats || {}, savesVersion);
      setCursor(result.nextCursor || null);
      return { request, savesVersion };
    } catch (requestError) { if (request === requestId.current) setError(readableError(requestError)); }
    finally {
      if (request === requestId.current) { pageRequestInFlight.current = false; setLoading(false); }
    }
  }, [api, filter, motion, autoFlaggedDirection, separateStats, saveQueue, adoptStats]);
  const refresh = useCallback(async () => {
    if (!separateStats) return load();
    const [cards, totals] = await Promise.all([load(), loadStats()]);
    // Only retire saved overlays once BOTH the cards and their matching totals
    // are fresh. A fast totals response must not resurrect an old card.
    if (cards && totals && cards.request === requestId.current && totals.request === statsRequestId.current &&
      cards.savesVersion === totals.savesVersion && saveQueue.acceptRefresh(cards.savesVersion)) renderSaves((value) => value + 1);
  }, [load, loadStats, separateStats, saveQueue]);
  useEffect(() => { loadedPages.current = 1; setTasks([]); setCursor(null); load(); return () => { requestId.current += 1; }; }, [load]);
  useEffect(() => {
    if (!separateStats) return undefined;
    loadStats();
    return () => { statsRequestId.current += 1; window.clearTimeout(statsRetryTimer.current); };
  }, [separateStats, loadStats]);
  useEffect(() => {
    let active = true;
    getDocs(collection(db, 'cameras')).then((snapshot) => {
      if (active) setCameras(snapshot.docs.map((item) => ({ ...item.data(), id: item.id })).sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)));
    }).catch((requestError) => { if (active) setCameraError(readableError(requestError)); });
    return () => { active = false; };
  }, []);
  const toggleCamera = async (camera, enabled) => {
    setCameraSaving(camera.id); setCameraError('');
    try {
      await updateDoc(doc(db, 'cameras', camera.id), { [cameraField]: enabled });
      setCameras((previous) => previous.map((item) => item.id === camera.id ? { ...item, [cameraField]: enabled } : item));
    } catch (requestError) { setCameraError(readableError(requestError)); }
    finally { setCameraSaving(''); }
  };
  const visibleTasks = motion ? saveQueue.project(tasks, filter) : tasks;
  const visibleCount = visibleTasks.length;
  useEffect(() => {
    const list = reviewList.current;
    if (!list || loading || error || !cursor || pageRequestInFlight.current || automaticPages.current.has(cursor)) return undefined;
    let active = true;
    let observedCard = null;
    const loadNext = () => {
      if (!active || pageRequestInFlight.current || automaticPages.current.has(cursor)) return;
      // A repeated cursor or observer notification must not cause a fetch loop.
      // Failed requests remain available through the manual Load more button.
      automaticPages.current.add(cursor);
      load(cursor);
    };
    const observer = window.IntersectionObserver ? new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNext();
    }, { threshold: 0 }) : null;
    const observeLastRow = () => {
      if (!active || pageRequestInFlight.current) return;
      // Read the actual responsive layout instead of assuming four clips per row.
      const tracks = window.getComputedStyle(list).gridTemplateColumns;
      const columns = tracks && tracks !== 'none' ? tracks.trim().split(/\s+/).length : 1;
      // Quick decisions remove cards before their saves finish. Keep the queue
      // topped up even if the whole last row disappears between observer calls.
      if (visibleCount <= columns) { loadNext(); return; }
      const lastRow = list.children[Math.floor((visibleCount - 1) / columns) * columns];
      if (lastRow !== observedCard) {
        observer?.disconnect();
        observedCard = lastRow;
        if (lastRow) observer?.observe(lastRow);
      }
    };
    observeLastRow();
    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(observeLastRow) : null;
    resizeObserver?.observe(list);
    window.addEventListener('resize', observeLastRow);
    return () => {
      active = false;
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', observeLastRow);
    };
  }, [cursor, error, load, loading, visibleCount]);
  const resolve = (decision) => {
    if (motion) { saveQueue.enqueue('resolve', decision, visibleTasks.find((task) => task.id === decision.taskId)); return; }
    return api.resolve(decision).then(async () => {
      setNotice('Staff decision saved. The original sighting has not been changed.');
      await load();
    });
  };
  const dismissFlags = (payload) => saveQueue.enqueue('dismiss', payload, visibleTasks.find((task) => task.id === payload.taskId));
  const displayedStats = motion && Number.isFinite(stats.flagged) ? { ...stats, flagged: Math.max(0, stats.flagged + saveQueue.flaggedDelta) } : stats;
  const displayedNotice = motion ? saveQueue.notice : notice;
  return <main className={`cr-admin-workspace${motion ? ' cr-motion-admin-workspace' : ''}`}>
    <div className="cr-admin-header"><div><h1>{motion ? 'Motion reviews' : 'Community reviews'}</h1><p className="cr-muted">Check outcomes and choose which cameras contribute {mediaName}.</p></div><button className="cr-primary" onClick={refresh} disabled={loading || statsLoading}><FiRefreshCw aria-hidden="true" /> Refresh</button></div>
    <div className="cr-admin-stats" aria-busy={separateStats ? statsLoading : loading}>
      {[['pending', `Pending ${mediaName}`], ['needsReview', 'Need staff attention'], ['closed', `Completed ${mediaName}`], ['reviewedToday', 'Reviews today (SAST)'], ...(motion ? [['flagged', 'Flagged sightings'], ['autoFlagged', 'Auto flagged']] : [])].map(([key, label]) => <div className="cr-admin-stat" key={key}>
        <div className="cr-admin-stat-value"><strong>{displayedStats[key] ?? '—'}</strong>{statChanges[key] !== undefined && <span className="cr-admin-stat-change" title="Change since previous refresh" aria-label={`${label}: ${statChanges[key] > 0 ? 'increased' : 'decreased'} by ${Math.abs(statChanges[key])} since previous refresh`}>{statChanges[key] > 0 ? '+' : '−'}{Math.abs(statChanges[key])}</span>}</div>
        <span>{label}</span>
      </div>)}
    </div>
    {statsError && <p className="cr-error" role="alert">Couldn’t refresh totals: {statsError}</p>}
    <p className="cr-small cr-muted">Oldest pending: {stats.oldestPendingAt ? `${formatCaptureTime(stats.oldestPendingAt)} SAST · ${pendingAge(stats.oldestPendingAt)}` : previousStats.current === null ? 'Loading…' : 'None'}. Pending includes cases that need staff attention.</p>
    <details className="cr-admin-camera-panel"><summary>Participating cameras · {cameras.filter((camera) => camera[cameraField] === true).length} enabled</summary>
      <p>{motion ? 'New motion clips from enabled cameras enter motion review. This setting is separate from YOLO review. Camera software must also support motion uploads.' : 'Only new automatic PTZ screenshots from enabled cameras enter community review. There is no daily limit. Enabling a camera does not import older images.'}</p>
      {cameraError && <p className="cr-error" role="alert">{cameraError}</p>}
      <div className="cr-camera-options">{cameras.map((camera) => <label key={camera.id}><input type="checkbox" checked={camera[cameraField] === true} disabled={Boolean(cameraSaving)} onChange={(event) => toggleCamera(camera, event.target.checked)} />{camera.name || camera.cameraName || camera.id}{cameraSaving === camera.id && ' · Saving…'}</label>)}</div>
    </details>
    {motion ? <div className="cr-motion-admin-toolbar"><div className="cr-motion-admin-filters" role="group" aria-label="Show motion cases">
      {MOTION_REVIEW_SECTIONS.map(([value, label]) => <div className="cr-motion-admin-section" key={value}>
        <button type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
        <a className="cr-motion-admin-review-link" href={buildAdminReviewUrl(value, value === 'autoFlagged' ? autoFlaggedDirection : 'all')} target="_blank" rel="noopener noreferrer" aria-label={`Review mode: ${label}`}>Review mode</a>
      </div>)}
    </div><span className="cr-small cr-muted">{filter === 'closed' ? 'Most recently completed first' : filter === 'flagged' ? 'Most recently tagged first' : filter === 'autoFlagged' ? 'Most recently flagged first' : 'Newest first'} · Staff decisions are final</span></div> : <>
    <label htmlFor="cr-case-filter" className="cr-small">Show cases</label><select id="cr-case-filter" className="cr-admin-filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
      <option value="needsReview">Needs staff review</option>{motion && <option value="flagged">Flagged sightings</option>}<option value="open">All open {mediaName}</option><option value="closed">Completed {mediaName}</option><option value="all">All {mediaName}</option>
    </select>
    </>}
    {motion && filter === 'autoFlagged' && <>
      <div className="cr-motion-admin-filters cr-motion-admin-subfilters" role="group" aria-label="Community and AI disagreement">
        {AUTO_FLAGGED_REVIEW_DIRECTIONS.map(([value, label]) => <div className="cr-motion-admin-section" key={value}>
          <button type="button" aria-pressed={autoFlaggedDirection === value} onClick={() => setAutoFlaggedDirection(value)}>{label}</button>
          <a className="cr-motion-admin-review-link" href={buildAdminReviewUrl('autoFlagged', value)} target="_blank" rel="noopener noreferrer" aria-label={`Review mode: ${label}`}>Review mode</a>
        </div>)}
      </div>
      <p className="cr-small cr-muted">Community agreement differs from the model prediction. Agree or disagree with the community’s decision, or remove the track.</p>
    </>}
    {(motion || displayedNotice) && <p className={`cr-success${motion ? ' cr-staff-notice' : ''}`} role="status" aria-atomic="true" title={displayedNotice || undefined}>{displayedNotice}</p>}
    {motion && saveQueue.failures.map((failure) => <div className="cr-error cr-staff-save-error" role="alert" key={`${failure.payload.taskId}-${failure.kind}`}><span>Couldn’t save {failure.kind === 'dismiss' ? 'flag dismissal' : 'motion decision'} for track {failure.payload.taskId.slice(0, 8)}: {readableError(failure.error)}</span><button type="button" className="cr-verdict" onClick={() => saveQueue.retry(failure)}>Retry save</button></div>)}
    {error && <p className="cr-error" role="alert">{error}</p>}
    {!loading && !error && !visibleTasks.length && <p className="cr-muted">No {mediaName} in this view yet.</p>}
    <div ref={reviewList} className={`cr-admin-list${motion ? ' cr-motion-grid' : ''}`}>{visibleTasks.map((task) => motion ? <MotionAdminCase key={task.id} task={task} communityDecisionActions={filter === 'autoFlagged'} showFlaggedTime={filter === 'flagged'} onResolve={resolve} onDismissFlags={dismissFlags} onRefresh={() => load()} /> : <AdminCase key={task.id} task={task} onResolve={resolve} onRefresh={() => load()} />)}</div>
    {loading && <p role="status" className="cr-muted">Loading community reviews…</p>}
    {cursor && <button className="cr-primary cr-admin-load-more" disabled={loading} onClick={() => load(cursor)}>Load more {mediaName}</button>}
  </main>;
}

export default function CommunityAdmin({ api = communityApi, motionApi = motionReviewApi, yoloEnabled = false }) {
  const [pipeline, setPipeline] = useState(yoloEnabled ? 'yolo' : 'motion');
  const role = useAuthStore((state) => state.role);
  const loading = useAuthStore((state) => state.isAccessLoading);
  if (loading) return <main className="community-admin"><p role="status">Checking staff access…</p></main>;
  if (role !== 'admin') return <main className="community-admin"><h1>Staff access required</h1><p>This page is available to Ting Vision administrators.</p></main>;
  return <div className="community-admin">{yoloEnabled && <div className="cr-admin-tabs" role="group" aria-label="Review pipeline">{[['yolo', 'YOLO review'], ['motion', 'Motion review']].map(([value, label]) => <button key={value} type="button" className="cr-verdict" aria-pressed={pipeline === value} onClick={() => setPipeline(value)}>{label}</button>)}</div>}<AdminWorkspace key={pipeline} pipeline={pipeline} api={pipeline === 'motion' ? motionApi : api} /></div>;
}
